import { useCallback, useEffect, useRef, useState } from 'react'

export interface HeaderMenuItemType {
  label: String
  visible: Boolean | undefined
  href: any
  external?: boolean
}

interface HeaderMenuProps {
  /** Items above the Sign Out row. An item labelled `divider` renders as a rule. */
  items?: HeaderMenuItemType[]
  /** Called with the item href when an item is picked. `external` items open in a new tab instead. */
  onSelect?: (href: string) => void
  onLogout: () => void
}

/**
 * The hamburger menu in the header.
 *
 * It used to open and close on raw hover, so a pointer that strayed a few pixels
 * off the panel closed it mid-read. Now:
 *  - leaving only *schedules* a close, and coming back within the grace period cancels it
 *  - clicking the button pins the menu open, so it ignores hover entirely
 *  - Escape, an outside click, or picking an item closes it
 */
const CLOSE_DELAY_MS = 500

export const HeaderMenu = ({ items = [], onSelect, onLogout }: HeaderMenuProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isPinned, setIsPinned] = useState(false)
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)

  const cancelPendingClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])

  const close = useCallback(() => {
    cancelPendingClose()
    setIsPinned(false)
    setIsOpen(false)
  }, [cancelPendingClose])

  useEffect(() => cancelPendingClose, [cancelPendingClose])

  useEffect(() => {
    if (!isOpen) return

    const onPointerDown = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) close()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen, close])

  // The list is long enough to run off a short window, so cap it where the
  // viewport ends and let it scroll from there.
  useEffect(() => {
    if (!isOpen) return

    const fitToViewport = () => {
      const top = panelRef.current?.getBoundingClientRect().top || 0
      setMaxHeight(Math.max(200, window.innerHeight - top - 16))
    }

    fitToViewport()
    window.addEventListener('resize', fitToViewport)
    return () => window.removeEventListener('resize', fitToViewport)
  }, [isOpen])

  const onMouseEnter = () => {
    cancelPendingClose()
    setIsOpen(true)
  }

  const onMouseLeave = () => {
    if (isPinned) return
    cancelPendingClose()
    closeTimer.current = setTimeout(() => setIsOpen(false), CLOSE_DELAY_MS)
  }

  const onToggle = () => {
    cancelPendingClose()
    if (isOpen && isPinned) {
      close()
      return
    }
    setIsOpen(true)
    setIsPinned(true)
  }

  const onPick = (item: HeaderMenuItemType) => {
    close()
    if (item.external) window.open(item.href, '_blank', 'noopener,noreferrer')
    else onSelect && onSelect(item.href)
  }

  // The bars go light while the button is dark, i.e. on hover as well as while the menu is open -
  // hover alone would otherwise leave navy bars on the navy hover background.
  const barClassName = `block rounded w-8 h-1 transition-colors group-hover:bg-gray-100 ${
    isOpen ? 'bg-gray-100' : 'bg-gray-600'
  }`
  const itemClassName =
    'block w-full text-left text-white font-medium rounded px-2 py-1.5 hover:bg-gray-500 hover:underline'

  return (
    <div
      ref={wrapperRef}
      className={`group relative rounded shadow hover:cursor-pointer hover:bg-gray-600 ${isOpen ? 'bg-gray-600' : ''}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <button
        type="button"
        className="block p-2 space-y-1.5 rounded"
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={onToggle}
      >
        <span className="sr-only">Open user menu</span>
        <span className={barClassName}></span>
        <span className={barClassName}></span>
        <span className={barClassName}></span>
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 pt-2 z-50" role="menu">
          <div
            ref={panelRef}
            className="p-3 bg-gray-600 w-72 rounded shadow-lg space-y-1 overflow-y-auto overscroll-contain"
            style={{ maxHeight }}
          >
            {items.map((item, index) => {
              if (!item.visible) return null
              if (item.label === 'divider')
                return <div key={`menu-${index}`} className="block rounded h-px bg-gray-400 my-2"></div>
              return (
                <button
                  type="button"
                  role="menuitem"
                  className={itemClassName}
                  key={`menu-${index}`}
                  onClick={() => onPick(item)}
                >
                  {item.label}
                </button>
              )
            })}
            <button
              type="button"
              role="menuitem"
              className={itemClassName}
              onClick={() => {
                close()
                onLogout()
              }}
            >
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
